/** WCAG relative luminance + contrast helpers for curated Mermaid palettes. */

function parseHexColor(hex: string): [number, number, number] {
  const normalized = hex.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    throw new Error(`Expected #RRGGBB color, got: ${hex}`);
  }
  return [
    Number.parseInt(normalized.slice(0, 2), 16),
    Number.parseInt(normalized.slice(2, 4), 16),
    Number.parseInt(normalized.slice(4, 6), 16),
  ];
}

function channelToLinear(channel: number): number {
  const value = channel / 255;
  return value <= 0.04045
    ? value / 12.92
    : ((value + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance (WCAG 2.x). */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = parseHexColor(hex).map(channelToLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Contrast ratio between two #RRGGBB colors (WCAG 2.x). */
export function contrastRatio(foreground: string, background: string): number {
  const lighter = Math.max(
    relativeLuminance(foreground),
    relativeLuminance(background),
  );
  const darker = Math.min(
    relativeLuminance(foreground),
    relativeLuminance(background),
  );
  return (lighter + 0.05) / (darker + 0.05);
}

/** WCAG 2.x AA normal-text minimum (1.4.3). Level A has no text contrast ratio. */
export const WCAG_AA_NORMAL_TEXT_RATIO = 4.5;

export function meetsWcagAaNormalText(
  foreground: string,
  background: string,
): boolean {
  return contrastRatio(foreground, background) >= WCAG_AA_NORMAL_TEXT_RATIO;
}

/**
 * Prefer `preferred` when it already meets AA against `background`;
 * otherwise pick the higher-contrast fallback.
 */
export function pickWcagAaTextColor(
  background: string,
  preferred: string,
  fallbackDark: string,
  fallbackLight: string,
): string {
  if (meetsWcagAaNormalText(preferred, background)) {
    return preferred;
  }
  const darkRatio = contrastRatio(fallbackDark, background);
  const lightRatio = contrastRatio(fallbackLight, background);
  return darkRatio >= lightRatio ? fallbackDark : fallbackLight;
}
